import MoreVertIcon from '@mui/icons-material/MoreVert';
import {Box, Button, ButtonGroup, Menu, MenuItem} from '@mui/material';
import React, {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface AdaptiveButtonGroupProps {
  children: React.ReactNode;
  disabled?: boolean;
}

export const AdaptiveButtonGroup: React.FC<AdaptiveButtonGroupProps> = ({
  children,
  disabled = false,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const measureRefs = useRef<Array<HTMLDivElement | null>>([]);
  const overflowMeasureRef = useRef<HTMLButtonElement | null>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [measuredWidths, setMeasuredWidths] = useState<number[]>([]);
  const [overflowBtnWidth, setOverflowBtnWidth] = useState<number>(44);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const childEls = useMemo(
    () =>
      Children.toArray(children).filter(isValidElement) as React.ReactElement[],
    [children]
  );

  const sorted = useMemo(() => {
    return [...childEls].sort((a, b) => {
      const pa =
        a.props['data-priority'] != null
          ? Number(a.props['data-priority'])
          : childEls.indexOf(a);
      const pb =
        b.props['data-priority'] != null
          ? Number(b.props['data-priority'])
          : childEls.indexOf(b);
      return pa - pb;
    });
  }, [childEls]);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }
    const ro = new ResizeObserver(() => {
      if (rootRef.current) {
        setContainerWidth(rootRef.current.offsetWidth);
      }
    });
    ro.observe(rootRef.current);
    setContainerWidth(rootRef.current.offsetWidth);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!sorted.length) {
      return;
    }
    // ensure array length
    measureRefs.current.length = sorted.length;

    const w = measureRefs.current.map((el) =>
      el ? Math.ceil(el.getBoundingClientRect().width) : 0
    );
    setMeasuredWidths(w);

    if (overflowMeasureRef.current) {
      setOverflowBtnWidth(
        Math.ceil(overflowMeasureRef.current.getBoundingClientRect().width)
      );
    }
  }, [sorted, containerWidth]);

  const {visible, hidden} = useMemo(() => {
    if (!measuredWidths.length || containerWidth <= 0) {
      return {visible: sorted, hidden: [] as React.ReactElement[]};
    }

    let left = containerWidth;
    const vis: React.ReactElement[] = [];
    const hid: React.ReactElement[] = [];

    for (let i = 0; i < sorted.length; i++) {
      const w = measuredWidths[i] ?? 40;
      const remaining = sorted.length - (i + 1);
      const needOverflow = remaining > 0 || hid.length > 0;
      const guard = needOverflow ? w + overflowBtnWidth : w;

      if (left >= guard) {
        left -= w;
        vis.push(sorted[i]);
      } else {
        hid.push(sorted[i]);
      }
    }
    return {visible: vis, hidden: hid};
  }, [sorted, measuredWidths, overflowBtnWidth, containerWidth]);

  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <Box
      ref={rootRef}
      sx={{width: '100%', minWidth: 0, display: 'inline-block'}}
    >
      <ButtonGroup disabled={disabled}>
        {visible.map((btn, idx) =>
          cloneElement(btn, {key: btn.key ?? `vis-${idx}`})
        )}
        {hidden.length > 0 && (
          <Button
            ref={overflowMeasureRef}
            size="small"
            onClick={handleOpen}
            disabled={disabled}
            sx={{
              minWidth: 'auto',
              px: 1,
            }}
          >
            <MoreVertIcon fontSize="small" />
          </Button>
        )}
      </ButtonGroup>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {hidden.map((btn, i) => {
          if (
            (btn.type as any)?.displayName === 'AutoRefreshSelect' ||
            (btn.type as any)?.name === 'AutoRefreshSelect' ||
            (btn.type as any)?.displayName === 'AutoRefreshSettingsSelect' ||
            (btn.type as any)?.name === 'AutoRefreshSettingsSelect'
          ) {
            return (
              <MenuItem
                key={btn.key ?? `hid-${i}`}
                sx={{
                  p: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {cloneElement(btn, {disabled})}
              </MenuItem>
            );
          }

          // Regular buttons
          const hasOnClick = Boolean(btn.props.onClick);

          if (!hasOnClick) {
            return (
              <MenuItem
                key={btn.key ?? `hid-${i}`}
                disabled={disabled || Boolean(btn.props.disabled)}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {cloneElement(btn)}
              </MenuItem>
            );
          }

          return (
            <MenuItem
              key={btn.key ?? `hid-${i}`}
              onClick={(e) => {
                if (btn.props.onClick) {
                  btn.props.onClick(e);
                }
                handleClose();
              }}
              disabled={disabled || Boolean(btn.props.disabled)}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {btn.props.children}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Hidden measuring layer */}
      <Box
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          height: 0,
        }}
      >
        <ButtonGroup
          disabled={disabled}
          sx={{
            '& .MuiButton-root': {minWidth: 0, px: 1},
          }}
        >
          {sorted.map((btn, idx) => (
            <Box
              key={`m-${btn.key ?? idx}`}
              ref={(el: HTMLDivElement | null) => {
                measureRefs.current[idx] = el;
              }}
              sx={{display: 'inline-block'}}
            >
              {cloneElement(btn, {disabled, key: undefined})}
            </Box>
          ))}
          <Button
            size="small"
            sx={{minWidth: 0, px: 1, '&.MuiButton-root': {minWidth: 0}}}
          >
            <MoreVertIcon fontSize="small" />
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
};
